import React, { useState } from "react";
import { BRAND, SERVICES } from "../mock/mock";
import { MapPin, Phone, Clock, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";
import { createBooking } from "../lib/api";

const TIMINGS = ["Immediately", "In 3 Days", "Within a Week", "Not Sure"];

export default function Contact() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", service: "", timing: "", message: "" });

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Please add your name & phone", description: "We need these to confirm your slot." });
      return;
    }
    try {
      await createBooking(form);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Booking save failed, continuing to WhatsApp:", err);
    }
    const text = `Hi ${BRAND.name}! I'd like to book:%0AName: ${form.name}%0APhone: ${form.phone}%0AService: ${form.service || "-"}%0AWhen: ${form.timing || "-"}%0AMessage: ${form.message || "-"}`;
    window.open(`https://wa.me/${BRAND.whatsapp}?text=${text}`, "_blank");
    toast({ title: "Thank you! ✨", description: "Our team will reach you on WhatsApp shortly." });
    setForm({ name: "", phone: "", service: "", timing: "", message: "" });
  };

  const inputCls =
    "w-full bg-viimii border hairline rounded-xl px-4 py-3.5 text-cream placeholder-cream/35 focus:outline-none focus:border-gold transition-colors";

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="reveal text-center max-w-2xl mx-auto mb-14">
          <p className="section-line text-gold mb-4">Contact Us</p>
          <h2 className="font-display text-4xl md:text-5xl font-medium leading-tight">
            Let's create your next <span className="gold-gradient-text italic">luxury look.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Info + map */}
          <div className="reveal space-y-4">
            {[
              [MapPin, "Studio Address", BRAND.address],
              [Phone, "Phone Number", BRAND.phone],
              [Clock, "Working Hours", BRAND.hours],
            ].map(([Icon, label, val]) => (
              <div key={label} className="flex gap-4 p-5 rounded-2xl border hairline bg-viimii-2">
                <span className="w-11 h-11 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(201,161,90,0.13)" }}>
                  <Icon size={18} className="text-gold" style={{ color: "var(--viimii-gold)" }} />
                </span>
                <div>
                  <div className="text-[11px] tracking-widest uppercase text-cream/45">{label}</div>
                  <div className="text-cream/85 mt-1 leading-relaxed">{val}</div>
                </div>
              </div>
            ))}
            <div className="rounded-2xl overflow-hidden border hairline h-[240px]">
              <iframe
                title="VIIMII Studio location"
                src={BRAND.mapEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.3) contrast(1.05)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} className="reveal p-7 md:p-9 rounded-[28px] border hairline bg-viimii-2">
            <h3 className="font-display text-2xl text-cream mb-6">Book your appointment</h3>
            <div className="space-y-4">
              <input className={inputCls} placeholder="Full Name" value={form.name} onChange={(e) => upd("name", e.target.value)} />
              <input className={inputCls} placeholder="Phone Number" value={form.phone} onChange={(e) => upd("phone", e.target.value)} />
              <select className={inputCls} value={form.service} onChange={(e) => upd("service", e.target.value)}>
                <option value="">Service interested in</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              <select className={inputCls} value={form.timing} onChange={(e) => upd("timing", e.target.value)}>
                <option value="">When do you plan to visit?</option>
                {TIMINGS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <textarea className={inputCls} rows={3} placeholder="Message (optional)" value={form.message} onChange={(e) => upd("message", e.target.value)} />
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-medium py-4 rounded-xl hover:opacity-90 transition-opacity"
                style={{ background: "var(--viimii-gold)" }}
              >
                <Send size={17} /> Send via WhatsApp
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
